const TURKISH_FRACTIONS = {
    'yarım': 0.5,
    'yarim': 0.5,
    'çeyrek': 0.25,
    'ceyrek': 0.25,
    'bir buçuk': 1.5,
    'bir bucuk': 1.5,
    'iki buçuk': 2.5,
    'iki bucuk': 2.5,
};

export const normalizeKey = (str) => {
    if (!str) return '';
    return str.toLowerCase().trim()
        .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ü/g, 'u')
        .replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g')
        .replace(/[^a-z0-9]/g, '');
};

import { formatQuantity as sharedFormatQuantity, isNearInteger } from './quantityFormatter';
export { isNearInteger };

// Authority Table (Rule UN-06)
export const DENSITY_OVERRIDES = {
    'un': { 'su_bardagi': 100, 'yemek_kasigi': 10 },
    'tozseker': { 'su_bardagi': 170, 'yemek_kasigi': 14, 'tatlikasigi': 8 },
    'tereyagi': { 'su_bardagi': 175, 'yemek_kasigi': 30, 'tatlikasigi': 5 },
    'zeytinyagi': { 'su_bardagi': 166, 'yemek_kasigi': 12 },
    'siviyag': { 'su_bardagi': 166, 'yemek_kasigi': 12 },
    'barbunya': { 'su_bardagi': 160 },
    'nohut': { 'su_bardagi': 170 },
    'kirmizimercimek': { 'su_bardagi': 180 },
    'yesilmercimek': { 'su_bardagi': 160 },
    'kurufasulye': { 'su_bardagi': 170 },
    'bulgur': { 'su_bardagi': 160, 'caybardagi': 80 },
    'salca': { 'yemek_kasigi': 25, 'tatlikasigi': 12 },
    'sut': { 'su_bardagi': 200, 'yemek_kasigi': 15 },
    'yogurt': { 'su_bardagi': 200, 'yemek_kasigi': 40 },
    'bal': { 'su_bardagi': 240, 'yemek_kasigi': 20 },
    'pekmez': { 'su_bardagi': 240, 'yemek_kasigi': 20 },
    'krema': { 'su_bardagi': 200, 'yemek_kasigi': 15 },
    'arpasehriye': { 'su_bardagi': 160 },
    'telsehriye': { 'su_bardagi': 150 },
    'sehriye': { 'su_bardagi': 160 },
    'pirinc': { 'su_bardagi': 180 },
    'irmik': { 'su_bardagi': 160 },
    'misirunu': { 'su_bardagi': 120 },
    'su': { 'su_bardagi': 200 }
};

export const PIECE_WEIGHTS = {
    'sogan': 150, 'domates': 120, 'havuc': 100, 'patates': 150, 'kabak': 250, 'patlican': 250,
    'sarimsak': 5, 'yumurta': 55
};

/**
 * Parses a string or number into a decimal.
 * Handles fractions like "1/2", "1 1/2", and Turkish commas "0,5".
 */
export function parseFraction(amount) {
    if (amount === null || amount === undefined || amount === '') return 0;
    if (typeof amount === 'number') return amount;

    const amountStr = amount.toString().toLowerCase().trim();
    if (TURKISH_FRACTIONS[amountStr]) return TURKISH_FRACTIONS[amountStr];

    const mixedMatch = amountStr.match(/(\d+)\s+(\d+)\/(\d+)/);
    if (mixedMatch) return parseInt(mixedMatch[1]) + (parseInt(mixedMatch[2]) / parseInt(mixedMatch[3]));

    const fractionMatch = amountStr.match(/(\d+)\/(\d+)/);
    if (fractionMatch) return parseInt(fractionMatch[1]) / parseInt(fractionMatch[2]);

    return parseFloat(amountStr.replace(',', '.')) || 0;
}

/**
 * Formats a decimal number with a comma as the decimal separator.
 * Used for inventory, shopping lists, and standard unit display.
 */
export function formatDecimal(amount) {
    if (amount === undefined || amount === null || isNaN(amount)) return '0';
    const num = Number(amount);

    // Round to 2 decimal places and replace dot with comma
    let str = (Math.round(num * 100) / 100).toString();
    return str.replace('.', ',');
}

/**
 * Formats a quantity using central quantityFormatter authority.
 */
export function formatQuantity(amount, originalText = null, scaleFactor = 1.0) {
    return sharedFormatQuantity(amount, originalText, scaleFactor);
}

/**
 * Formats a number as Turkish Lira (1.800,30 ₺).
 */
export function formatCurrency(amount) {
    if (amount === undefined || amount === null || isNaN(amount)) return '0,00 ₺';
    const num = Number(amount);

    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num)
        .replace('₺', '') // Clear the default placement
        .trim() + ' ₺'; // Add at the end with a space
}

/**
 * Formats a total summary amount (rounded to nearest ₺, no kuruş).
 * Example: 2.814,87 -> 2.815 ₺
 */
export function formatTotalCurrency(amount) {
    if (amount === undefined || amount === null || isNaN(amount)) return '0 ₺';
    const num = Math.round(Number(amount));

    return new Intl.NumberFormat('tr-TR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num) + ' ₺';
}

/**
 * Formats a quantity for culinary display (rounding to human-friendly fractions/integers).
 * Law: Priority is readability over lab-grade precision.
 */
export function formatCulinaryQuantity(amount, unit) {
    if (amount === undefined || amount === null || isNaN(amount)) return '0';
    let num = Number(amount);
    
    const lowerUnit = (unit || '').toLowerCase().trim();

    // 1. Piece Check (adet, tane, diş) - High tolerance for rounding to integers/halves
    const pieceUnits = ['adet', 'tane', 'diş', 'dis'];
    if (pieceUnits.includes(lowerUnit)) {
        // [V12.145] Near integer? (Allow ~40% deviation to favor whole produce)
        // This ensures 1.6 adet -> 2 adet, 2.4 adet -> 2 adet. 
        if (Math.abs(num - Math.round(num)) < 0.41) {
            return Math.round(num).toString();
        }
        // Round to nearest 0.5
        const roundedHalf = Math.round(num * 2) / 2;
        return formatQuantity(roundedHalf);
    }

    // 2. Volume Units (su bardağı, yemek kaşığı, etc.) - Round to 0.25 (1/4) increments
    const volumeUnits = ['su bardağı', 'su bardagi', 'yemek kaşığı', 'yemek kasigi', 'tatlı kaşığı', 'tatli kasigi', 'çay kaşığı', 'cay kasigi', 'cay_bardagi', 'çay bardağı'];
    if (volumeUnits.some(v => lowerUnit.includes(v))) {
        // Special case: if it's very close to 1 (like 0.8+), round up to 1
        if (num > 0.82 && num < 1.18) return '1';
        
        // Round to nearest 0.25
        const roundedQuarter = Math.round(num * 4) / 4;
        return formatQuantity(roundedQuarter);
    }

    // 3. Weight/Volume (g, ml) - Allow standard technical formatting
    return formatQuantity(num);
}
/**
 * Calculates the current value of an inventory batch based on remaining amount.
 * formula: (remaining / initial) * purchasePrice
 */
export function calculateBatchValue(item, priceMap = null) {
    if (!item) return 0;

    let purchasePrice = parseFloat(item.purchasePrice || item.purchase_price || 0);
    const dictionaryId = item.ingredient?.id;

    // If a priceMap is provided, try to use it as the source of truth.
    if (priceMap && dictionaryId && priceMap.has(String(dictionaryId))) {
        const unitPrice = parseFloat(priceMap.get(String(dictionaryId)));
        const initialAmount = parseFloat(item.initialAmount || item.initial_amount || item.remaining_amount || 0);
        
        // This is a simplification. A full solution would need robust unit conversion
        // (e.g., converting 'adet' to 'kg' using PIECE_WEIGHTS).
        // For now, we assume the initialAmount is in the canonical unit of the price map.
        if (!isNaN(unitPrice) && !isNaN(initialAmount)) {
            purchasePrice = unitPrice * initialAmount;
        }
    }
    
    if (isNaN(purchasePrice) || purchasePrice === 0) return 0;

    // Use raw amounts when available to avoid ratio corruption after display normalization (g<->kg, ml<->lt).
    const initial = parseFloat(
        item.initialAmountRaw ||
        item.initial_amount_raw ||
        item.initialAmount ||
        item.initial_amount ||
        0
    );
    const remaining = parseFloat(
        item.remainingAmountRaw ||
        item.remaining_amount_raw ||
        item.remainingAmount ||
        item.remaining_amount ||
        0
    );

    // Use ratio if initial exists, otherwise return purchasePrice
    if (initial > 0) {
        const ratio = remaining / initial;
        return parseFloat((purchasePrice * ratio).toFixed(2));
    }

    return purchasePrice;
}
